import { Icon16DropdownOutline } from '@vkontakte/icons';
import classNames from 'clsx';
import React, { memo, ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { dispatchSearchRadarToolbarAction } from 'reactApp/modules/dwh/dwh.module';
import { DropdownItemAction } from 'reactApp/ui/DropdownItemAction/DropdownItemAction';
import { DropdownFont, DropdownList, DropdownTheme } from 'reactApp/ui/DropdownList/DropdownList';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { RegisterCloudTooltip } from 'reactApp/ui/RegisterCloudTooltip/RegisterCloudTooltip';
import { EToolbarItemTooltip, IToolbarItemProps, TToolbarDropdownItem } from 'reactApp/ui/Toolbar/Toolbar.types';
import { findDropdownPosition } from 'reactApp/ui/Toolbar/ToolbarItem/ToolbarItem.utils';
import { ChevronDownIcon } from 'reactApp/ui/VKUIIcons';
import { noopVoid } from 'reactApp/utils/helpers';
import { ECategoryGa } from 'reactApp/utils/paymentGa';

import styles from './ToolbarItem.css';

export const ToolbarItem = memo(
    ({
        id,
        promoId,
        text = '',
        hint = '',
        collapse = false,
        icon,
        list,
        withoutDropdownIcon,
        dropdownTheme,
        dropdownPosition,
        openDropdownByArrow = false,
        onClick = noopVoid,
        parentRef,
        mod,
        tooltipId,
        maxDropdownListHeight,
        isDropdownListOpen,
        setIsDropdownListOpen,
        renderDropdownListItem,
    }: IToolbarItemProps): ReactElement => {
        // @fixme CLOUDWEB-14062
        const isCreate = id === 'create';
        const rootRef = useRef<HTMLDivElement>(null);

        const [isOpen, setIsOpen] = useState<boolean>(false);

        const dispatch = useDispatch();

        const isDropdownOpen = useMemo(() => isDropdownListOpen ?? isOpen, [isOpen, isDropdownListOpen]);
        const toggleIsDropdownOpen = useCallback(
            (majorIsDropdownOpen?: boolean) => {
                if (setIsDropdownListOpen) {
                    const result = majorIsDropdownOpen ?? !isDropdownOpen;
                    setIsDropdownListOpen(result);
                    return;
                }

                const result = majorIsDropdownOpen ?? !isDropdownOpen;
                setIsOpen(result);
            },
            [isDropdownListOpen, setIsDropdownListOpen, isOpen, setIsOpen]
        );

        const tooltip = useMemo(() => {
            if (tooltipId === EToolbarItemTooltip.publicCreateCloud) {
                return <RegisterCloudTooltip targetRef={rootRef} closeOnScroll />;
            }
            return null;
        }, [tooltipId, rootRef?.current]);

        const renderDropdownItem = useCallback(
            ({ text, active, icon, mod, hotkeyLabel, asc, withStatus }: TToolbarDropdownItem) => (
                <DropdownItemAction
                    text={text}
                    active={active}
                    icon={icon}
                    mod={mod}
                    hotkey={hotkeyLabel}
                    asc={asc}
                    withStatus={withStatus}
                />
            ),
            []
        );

        const content = useMemo(
            () => (
                <div className={styles.content}>
                    {icon && <div className={styles.icon}>{icon}</div>}
                    {text && !collapse && <div className={styles.text}>{text}</div>}
                </div>
            ),
            [collapse, icon, text]
        );

        const closeDropdown = useCallback(() => {
            toggleIsDropdownOpen(false);
        }, [toggleIsDropdownOpen]);

        const onItemClick = useCallback(() => {
            if (list && !openDropdownByArrow) {
                toggleIsDropdownOpen();
            }

            const onSuccess = (params) => {
                if (params.source === 'search') {
                    dispatch(dispatchSearchRadarToolbarAction({ ...params, eventCategory: ECategoryGa.toolbar_search }));
                }
            };

            onClick(undefined, onSuccess);
        }, [list, onClick, openDropdownByArrow, toggleIsDropdownOpen, dispatch]);

        const onArrowClick = useCallback(
            (event) => {
                if (!openDropdownByArrow) {
                    return;
                }

                event.stopPropagation();
                toggleIsDropdownOpen();
            },
            [toggleIsDropdownOpen, openDropdownByArrow]
        );

        return (
            <>
                <div
                    className={classNames(styles.root, styles[`root_${id}`], {
                        [styles.root_active]: isDropdownOpen,
                        [styles.root_collapsed]: collapse || !text,
                        [styles.root_withDropdown]: list && !withoutDropdownIcon,
                        [styles[`root_${mod}`]]: !!mod,
                    })}
                    ref={rootRef}
                    onClick={onItemClick}
                    data-name={id}
                    data-promo-id={promoId}
                >
                    {hint || collapse ? <Hint text={collapse ? text : hint}>{content}</Hint> : content}
                    {list && (
                        <>
                            {!withoutDropdownIcon && (
                                <div
                                    className={classNames(styles.dropdownIcon, { [styles.dropdownIcon_bordered]: openDropdownByArrow })}
                                    onClick={onArrowClick}
                                >
                                    {isCreate ? <Icon16DropdownOutline /> : <ChevronDownIcon />}
                                </div>
                            )}
                            {isDropdownOpen && (
                                <DropdownList
                                    list={list}
                                    renderItem={renderDropdownListItem || renderDropdownItem}
                                    close={closeDropdown}
                                    calcPosition={findDropdownPosition({ ref: rootRef, parentRef, position: dropdownPosition })}
                                    theme={dropdownTheme || DropdownTheme.small}
                                    parentRef={rootRef}
                                    gaId="toolbar"
                                    closeOnResize
                                    closeOnScroll
                                    font={DropdownFont.mailSans}
                                    animated
                                    maxDropdownListHeight={maxDropdownListHeight}
                                />
                            )}
                        </>
                    )}
                </div>
                {tooltip}
            </>
        );
    }
);

ToolbarItem.displayName = 'NewToolbarItem';
